/** @fileoverview Pure helpers for Energy Source settings. */

import { parseTags } from "../Components/TagFilterUtils";

/**
 * Safe JSON.parse for localStorage reads — bad/missing values fall back.
 * @param {string|null} raw Raw stored value.
 * @param {*} [fallback] Value returned on missing/invalid input.
 * @returns {*} Parsed value or the fallback.
 */
export function safeParseJSON(raw, fallback = null) {
  if (raw == null) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Normalise a raw energy-source row (handles Sequelize dataValues wrappers).
 * @param {object} raw Source row from the API.
 * @returns {{id:number, name:string, source:string}} Normalised source.
 */
export function normaliseSource(raw) {
  const dv = raw?.dataValues ?? raw;
  return { id: dv.id, name: dv.name, source: dv.source };
}

/**
 * The mappable KPI set: every ENERGY-typed question (energy has no separate selection setting).
 * @param {object[]} questions Reporting questions.
 * @returns {Set<number>} Question ids whose kpi_type includes "ENERGY".
 */
export function energyKpiIds(questions) {
  return new Set(
    questions.filter(q => q.kpi_type?.includes("ENERGY")).map(q => q.questionId),
  );
}

/**
 * Diff current selection + source map against the last saved state and return
 * only the changed KPI→source mappings (a deselected/cleared KPI yields null).
 * @param {Set<number>} pendingIds Currently selected KPI ids.
 * @param {Record<number, number>} kpiSourceMap Current per-KPI source assignments.
 * @param {Record<number, number>} savedKpiSourceMap Last persisted assignments.
 * @returns {Array<{kpiId:number, energySourceId:(number|null)}>} Changed mappings.
 */
export function buildChangedMappings(pendingIds, kpiSourceMap, savedKpiSourceMap) {
  const allKpiIds = new Set([
    ...Object.keys(kpiSourceMap).map(Number),
    ...Object.keys(savedKpiSourceMap).map(Number),
  ]);

  const changed = [];
  for (const kpiId of allKpiIds) {
    const prev = savedKpiSourceMap[kpiId] ?? null;
    const next = pendingIds.has(kpiId) ? (kpiSourceMap[kpiId] ?? null) : null;
    if (prev !== next) changed.push({ kpiId, energySourceId: next });
  }
  return changed;
}

/**
 * Build a tag trie from questions for the drill-down filter.
 * @param {object[]} questions Questions to index.
 * @returns {{children:Map, kpiIds:Set<number>}} Trie root.
 */
export function buildTrie(questions) {
  const root = { children: new Map(), kpiIds: new Set() };
  for (const q of questions) {
    const combos = parseTags(q.tags);
    for (const combo of combos)
      if (combo && combo.length) insertCombo(root, combo, q.questionId);
  }
  return root;
}

/**
 * Insert one tag combo into the trie.
 * @param {object} node Current trie node.
 * @param {Array<{key:string, value:string}>} combo Remaining tag path.
 * @param {number} qId Question id to record along the path.
 */
export function insertCombo(node, combo, qId) {
  node.kpiIds.add(qId);
  if (!combo.length) return;
  const tag = combo[0];
  if (!tag?.key || !tag?.value) return;
  if (!node.children.has(tag.key)) node.children.set(tag.key, new Map());
  const km = node.children.get(tag.key);
  if (!km.has(tag.value)) km.set(tag.value, { children: new Map(), kpiIds: new Set() });
  insertCombo(km.get(tag.value), combo.slice(1), qId);
}

/**
 * Traverse the trie along a path of {key,value} steps, returning reached nodes.
 * @param {object} trie Trie root.
 * @param {Array<{key:string, value:string}>} steps Path steps.
 * @returns {object[]} Nodes reached by the path (empty when unreachable).
 */
export function traverseTrieMulti(trie, steps) {
  let nodes = [trie];
  for (const step of steps) {
    if (!step?.key || !step?.value) continue;
    const next = [];
    for (const node of nodes) {
      const km = node.children?.get(step.key);
      if (!km) continue;
      const c = km.get(step.value);
      if (c) next.push(c);
    }
    if (!next.length) return [];
    nodes = next;
  }
  return nodes;
}

/**
 * Collect the available next keys (and their values) below a set of nodes.
 * @param {object[]} nodes Current trie nodes.
 * @param {Set<string>} usedKeys Keys already consumed on the path.
 * @returns {Array<{key:string, values:string[]}>} Available keys with sorted values.
 */
export function getAvailableKeysMulti(nodes, usedKeys) {
  const kvm = new Map();
  for (const node of nodes) {
    if (!node?.children) continue;
    for (const [key, vm] of node.children.entries()) {
      if (usedKeys.has(key)) continue;
      if (!kvm.has(key)) kvm.set(key, new Set());
      for (const v of vm.keys()) kvm.get(key).add(v);
    }
  }
  return Array.from(kvm.entries()).map(([key, values]) => ({ key, values: Array.from(values).sort() }));
}

/**
 * Traverse the trie along a path of {key, values:Set} steps, following every
 * selected value at each level. Strict positional match (no gaps): a step must
 * line up with the next combo position. Returns the nodes reached (empty when
 * the path is unreachable).
 * @param {object} trie Trie root.
 * @param {Array<{key:string, values:Set<string>}>} steps Multi-value path steps.
 * @returns {object[]} Nodes reached by the path.
 */
export function traverseTrieMultiValue(trie, steps) {
  let nodes = [trie];
  for (const step of steps) {
    const next = [];
    for (const node of nodes) {
      const vm = node.children?.get(step?.key);
      if (!vm) continue;
      for (const value of step?.values ?? []) {
        const child = vm.get(value);
        if (child) next.push(child);
      }
    }
    if (!next.length) return [];
    nodes = next;
  }
  return nodes;
}

/**
 * From a set of trie nodes, collect the distinct question ids reaching each
 * next value under `key`. Unions across nodes so a question arriving via several
 * branches is counted once.
 * @param {object[]} nodes Trie nodes to read children from.
 * @param {string} key Tag key of the next level.
 * @returns {Map<string, Set<number>>} Next value → distinct question ids.
 */
export function collectNextValueIds(nodes, key) {
  const byValue = new Map();
  for (const node of nodes) {
    const vm = node.children?.get(key);
    if (!vm) continue;
    for (const [value, child] of vm.entries()) {
      let ids = byValue.get(value);
      if (!ids) { ids = new Set(); byValue.set(value, ids); }
      for (const id of child.kpiIds) ids.add(id);
    }
  }
  return byValue;
}
